/**
 * ModelSpecSyncBridge — effect-based bridge between Model Spec changes and UI Model state.
 *
 * Watches userProjectDataModels (from ModuleContext) for changes. When fields,
 * categories, connectors, or submodels are deleted/renamed in the Model tab,
 * dispatches APPLY_SYNC to remove orphaned items from the canvas and notify the user.
 *
 * Follows the CanvasAutoSave pattern: renders nothing, pure side-effect component.
 */

import { useEffect, useRef } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { useModule } from '../contexts/ModuleContext';
import { useUIModelState } from '../contexts/UIModelStateContext';
import {
  syncUIModelWithModelSpec,
  shouldSync,
  generateSyncNotifications,
} from '../sync/modelSpecSync';
import { TUpsertISRProjectDataModelsInput } from '../../Pages/DefineModel/model.d';
import type { UIModelAPI } from '../types/api.types';
import type { UIModelState } from '../types/state.types';

/** Sentinel value to distinguish "never set" from "null spec" */
const UNINITIALIZED = Symbol('UNINITIALIZED');

type PrevSpecRef = TUpsertISRProjectDataModelsInput | null | typeof UNINITIALIZED;

/**
 * Run sync, dispatch APPLY_SYNC if changes found, and show toast notifications.
 * Shared between Effect 1 (spec change) and Effect 2 (model load).
 */
const applySyncIfNeeded = (
  currentState: UIModelState,
  spec: TUpsertISRProjectDataModelsInput | null,
  dispatch: React.Dispatch<any>,
): void => {
  const { updatedState, syncResult } = syncUIModelWithModelSpec(
    currentState,
    spec
  );

  const hasChanges =
    syncResult.removedItems.length > 0 ||
    syncResult.updatedItems.length > 0 ||
    syncResult.orphanedContainers.length > 0 ||
    syncResult.updatedContainers.length > 0;

  if (!hasChanges) return;

  const affectedCanvasIds = updatedState.model?.layout?.canvas
    ? Object.keys(updatedState.model.layout.canvas)
    : [];

  dispatch({
    type: 'APPLY_SYNC',
    payload: {
      model: updatedState.model,
      affectedCanvasIds,
    },
  });

  const { info, warnings } = generateSyncNotifications(syncResult);
  info.forEach((msg) => toast.info(msg));
  warnings.forEach((msg) => toast.warning(msg));
};

const ModelSpecSyncBridge = () => {
  const { userProjectDataModels } = useModule();
  const { state, dispatch } = useUIModelState();

  const prevSpecRef = useRef<PrevSpecRef>(UNINITIALIZED);
  const prevModelRef = useRef<UIModelAPI | null>(null);

  // Effect 1: Spec changes while model is loaded (existing behavior)
  useEffect(() => {
    // Guard 1: First capture — store baseline, don't sync
    if (prevSpecRef.current === UNINITIALIZED) {
      prevSpecRef.current = userProjectDataModels;
      return;
    }

    // Guard 2: No model loaded yet — nothing to sync against
    if (!state.model) {
      prevSpecRef.current = userProjectDataModels;
      return;
    }

    // Guard 3: No actual change in Model Spec
    if (!shouldSync(prevSpecRef.current, userProjectDataModels)) {
      return;
    }

    // Store new baseline before processing
    prevSpecRef.current = userProjectDataModels;

    applySyncIfNeeded(state, userProjectDataModels, dispatch);
  }, [userProjectDataModels]); // Only trigger on spec changes — state/dispatch are stable refs

  // Effect 2: Model just loaded — reconcile against current spec
  // Handles the case where the component remounts (tab switch) and the model
  // loads from API with stale data that doesn't match the current spec.
  useEffect(() => {
    const wasNull = prevModelRef.current === null;
    prevModelRef.current = state.model;

    // Only run on null → loaded transition (model just loaded)
    if (!wasNull || !state.model) return;

    // No spec to compare against
    if (!userProjectDataModels) return;

    // Update spec baseline so Effect 1 doesn't re-trigger for the same spec
    prevSpecRef.current = userProjectDataModels;

    applySyncIfNeeded(state, userProjectDataModels, dispatch);
  }, [state.model]); // Only trigger on model changes

  return null;
};

export default ModelSpecSyncBridge;
